/**
 * Planet terrain surface queries.
 *
 * The physics surface is the SAME flat-shaded triangle mesh the renderer
 * draws: each vertex i sits at basePositions[i] + normalize(basePositions[i]) *
 * heightmap[i], and consecutive vertex triplets form triangles (non-indexed
 * geometry). Surface height in a direction is found by casting a ray from the
 * planet centre through the displaced triangles — exact, not nearest-vertex.
 *
 * A per-planet spatial index (cube-map bins → triangle lists) makes a query
 * O(bin size) instead of O(vertex count). The index depends only on
 * basePositions, which never change, so it is cached by array identity.
 *
 * Planets whose basePositions are NOT a triangle mesh (e.g. the 42-point
 * Fibonacci spheres used by test helpers) are detected and fall back to the
 * legacy nearest-vertex lookup.
 *
 * Mirrored in wasm-physics/src/lib.rs — the two must produce identical results.
 */

import type { Vec3, PlanetState } from '../types';
import { MAX_TERRAIN_HEIGHT_FRACTION } from '../constants';
import { simWork } from './workCounters';

// =============================================================================
// Spatial index
// =============================================================================

export interface TerrainIndex {
  valid: boolean;        // false → basePositions is not a triangle mesh
  triCount: number;
  n: number;             // bins per cube-face edge
  binStart: Uint32Array; // CSR offsets, length 6*n*n + 1
  binTris: Uint32Array;  // triangle indices
  invLen: Float64Array;  // 1 / |basePositions[i]| per vertex
  /** 62.51: the triangle the last lookup hit (a hint: it changes speed, never a result). */
  lastHit?: number;
}

const indexCache = new WeakMap<Float32Array, TerrainIndex>();

/** Cube-map bin for a direction. Must match lib.rs dir_to_bin exactly. */
function dirToBin(x: number, y: number, z: number, n: number): number {
  const ax = Math.abs(x), ay = Math.abs(y), az = Math.abs(z);
  let face: number, u: number, v: number;
  if (ax >= ay && ax >= az) {
    face = x >= 0 ? 0 : 1; u = y / ax; v = z / ax;
  } else if (ay >= az) {
    face = y >= 0 ? 2 : 3; u = x / ay; v = z / ay;
  } else {
    face = z >= 0 ? 4 : 5; u = x / az; v = y / az;
  }
  let bu = Math.floor((u + 1) * 0.5 * n);
  let bv = Math.floor((v + 1) * 0.5 * n);
  if (bu < 0) bu = 0; else if (bu >= n) bu = n - 1;
  if (bv < 0) bv = 0; else if (bv >= n) bv = n - 1;
  return (face * n + bu) * n + bv;
}

/** Project a direction onto a given cube face, returning bin coords (unclamped). */
function projectToFace(face: number, x: number, y: number, z: number, n: number): [number, number] {
  let m: number, u: number, v: number;
  if (face <= 1) { m = Math.max(Math.abs(x), 1e-9); u = y / m; v = z / m; }
  else if (face <= 3) { m = Math.max(Math.abs(y), 1e-9); u = x / m; v = z / m; }
  else { m = Math.max(Math.abs(z), 1e-9); u = x / m; v = y / m; }
  return [Math.floor((u + 1) * 0.5 * n), Math.floor((v + 1) * 0.5 * n)];
}

function faceOf(x: number, y: number, z: number): number {
  const ax = Math.abs(x), ay = Math.abs(y), az = Math.abs(z);
  if (ax >= ay && ax >= az) return x >= 0 ? 0 : 1;
  if (ay >= az) return y >= 0 ? 2 : 3;
  return z >= 0 ? 4 : 5;
}

function isTriangleMesh(bp: Float32Array, vertexCount: number): boolean {
  if (vertexCount < 12 || vertexCount % 3 !== 0) return false;
  // A real non-indexed mesh shares every position between several triangles.
  const seen = new Set<string>();
  for (let i = 0; i < vertexCount; i++) {
    const x = bp[i * 3], y = bp[i * 3 + 1], z = bp[i * 3 + 2];
    seen.add(`${Math.round(x * 1e4)},${Math.round(y * 1e4)},${Math.round(z * 1e4)}`);
  }
  return seen.size < vertexCount * 0.5;
}

export function getTerrainIndex(basePositions: Float32Array): TerrainIndex {
  const cached = indexCache.get(basePositions);
  if (cached) return cached;

  const vertexCount = basePositions.length / 3;
  const triCount = Math.floor(vertexCount / 3);
  const invLen = new Float64Array(vertexCount);
  for (let i = 0; i < vertexCount; i++) {
    const x = basePositions[i * 3], y = basePositions[i * 3 + 1], z = basePositions[i * 3 + 2];
    const len = Math.sqrt(x * x + y * y + z * z);
    invLen[i] = len > 0 ? 1 / len : 0;
  }

  const valid = isTriangleMesh(basePositions, vertexCount);
  if (!valid) {
    const idx: TerrainIndex = { valid: false, triCount, n: 1, binStart: new Uint32Array(7), binTris: new Uint32Array(0), invLen };
    indexCache.set(basePositions, idx);
    return idx;
  }

  const n = Math.max(4, Math.ceil(Math.sqrt(triCount / 6) / 1.5));
  const binCount = 6 * n * n;
  const counts = new Uint32Array(binCount);

  // Two passes (count, fill) — same insertion rule each time.
  const visit = (tri: number, cb: (bin: number) => void) => {
    const faces: number[] = [];
    const dirs: number[][] = [];
    for (let k = 0; k < 3; k++) {
      const vi = tri * 3 + k;
      const x = basePositions[vi * 3] * invLen[vi];
      const y = basePositions[vi * 3 + 1] * invLen[vi];
      const z = basePositions[vi * 3 + 2] * invLen[vi];
      dirs.push([x, y, z]);
      const f = faceOf(x, y, z);
      if (!faces.includes(f)) faces.push(f);
    }
    for (const f of faces) {
      let u0 = Infinity, u1 = -Infinity, v0 = Infinity, v1 = -Infinity;
      for (const [x, y, z] of dirs) {
        const [bu, bv] = projectToFace(f, x, y, z, n);
        if (bu < u0) u0 = bu; if (bu > u1) u1 = bu;
        if (bv < v0) v0 = bv; if (bv > v1) v1 = bv;
      }
      u0 = Math.max(0, u0 - 1); u1 = Math.min(n - 1, u1 + 1);
      v0 = Math.max(0, v0 - 1); v1 = Math.min(n - 1, v1 + 1);
      for (let bu = u0; bu <= u1; bu++) {
        for (let bv = v0; bv <= v1; bv++) {
          cb((f * n + bu) * n + bv);
        }
      }
    }
  };

  for (let t = 0; t < triCount; t++) visit(t, bin => { counts[bin]++; });

  const binStart = new Uint32Array(binCount + 1);
  for (let b = 0; b < binCount; b++) binStart[b + 1] = binStart[b] + counts[b];
  const binTris = new Uint32Array(binStart[binCount]);
  const fill = new Uint32Array(binCount);
  for (let t = 0; t < triCount; t++) {
    visit(t, bin => { binTris[binStart[bin] + fill[bin]] = t; fill[bin]++; });
  }

  const idx: TerrainIndex = { valid: true, triCount, n, binStart, binTris, invLen };
  indexCache.set(basePositions, idx);
  return idx;
}

// =============================================================================
// Surface radius
// =============================================================================

const BARY_EPS = 1e-6;

/**
 * Surface radius (distance from planet centre to the terrain mesh) in a
 * direction, from raw arrays. Exact ray/triangle intersection against the
 * displaced mesh; falls back to nearest vertex for non-mesh data.
 */
/**
 * The mesh triangle the last getSurfaceRadius / getSurfaceRadiusRaw call hit (-1 = a vertex
 * fallback). A scratch output for surface navigation, which needs to know where the piecewise-
 * linear terrain changes slope; it never changes a returned height.
 */
let lastTriangle = -1;
export function lastSurfaceTriangle(): number { return lastTriangle; }

/**
 * 62.51: mesh triangle `t` displaced by `heightmap`, its vertices into `out` (p0, p1, p2: nine numbers, from the planet
 * centre) — the same displaced positions the lookup intersects (rayTriangle's arithmetic).
 */
export function displacedTriangle(heightmap: Float32Array, bp: Float32Array, inv: Float64Array | Float32Array, t: number, out: Float64Array): void {
  for (let k = 0; k < 3; k++) {
    const i = t * 3 + k, s = 1 + heightmap[i] * inv[i];
    out[k * 3] = bp[i * 3] * s; out[k * 3 + 1] = bp[i * 3 + 1] * s; out[k * 3 + 2] = bp[i * 3 + 2] * s;
  }
}

const marchTri = new Float64Array(9);
/** marchClearSteps' spans, longest first (62.57: one array, not a literal a call). */
const MARCH_SPANS = [64, 16, 4] as const;
/**
 * 62.51: how many march steps after point a (planet-relative; step vector s) are known to be outside the ground: the
 * points a + s·j, j ≤ steps + 1 (a step's slack for t's rounding), all over triangle `tri` (its cone is convex, so both
 * ends well inside means the stretch is) and above its plane — where the ground's radius along a ray through p is
 * |p|·(N·p0)/(N·p), so the height over it is at least min|p| · min N·(p − p0) / max N·p. 0 when none is shown.
 */
function marchClearSteps(planet: PlanetState, idx: TerrainIndex, tri: number, ax: number, ay: number, az: number, sx: number, sy: number, sz: number): number {
  displacedTriangle(planet.heightmap, planet.basePositions, idx.invLen, tri, marchTri);
  const T = marchTri;
  const e1x = T[3] - T[0], e1y = T[4] - T[1], e1z = T[5] - T[2], e2x = T[6] - T[0], e2y = T[7] - T[1], e2z = T[8] - T[2];
  let nx = e1y * e2z - e1z * e2y, ny = e1z * e2x - e1x * e2z, nz = e1x * e2y - e1y * e2x;
  let np0 = nx * T[0] + ny * T[1] + nz * T[2];
  if (np0 < 0) { nx = -nx; ny = -ny; nz = -nz; np0 = -np0; }
  if (!(np0 > 0)) return 0;
  const margin = 1e-9 * planet.radius + 1e-12;
  for (const steps of MARCH_SPANS) {
    const j = steps + 1;
    const bx = ax + sx * j, by = ay + sy * j, bz = az + sz * j;
    if (!rayWellInside(T, ax, ay, az) || !rayWellInside(T, bx, by, bz)) continue;
    const npa = nx * ax + ny * ay + nz * az, npb = nx * bx + ny * by + nz * bz;
    const ha = npa - np0, hb = npb - np0;
    if (!(ha > 0 && hb > 0)) continue;
    const qx = bx - ax, qy = by - ay, qz = bz - az, qq = qx * qx + qy * qy + qz * qz;
    const w = qq > 0 ? Math.max(0, Math.min(1, -(ax * qx + ay * qy + az * qz) / qq)) : 0;
    const cx = ax + qx * w, cy = ay + qy * w, cz = az + qz * w;
    if ((Math.sqrt(cx * cx + cy * cy + cz * cz) * Math.min(ha, hb)) / Math.max(npa, npb) > margin) return steps;
  }
  return 0;
}

/** 62.51: is the ray from the centre through (x, y, z) inside triangle `tri` (nine numbers) by HINT_MARGIN — where the lookup can hit only it? */
export function rayWellInside(tri: Float64Array, x: number, y: number, z: number): boolean {
  const p0x = tri[0], p0y = tri[1], p0z = tri[2];
  const e1x = tri[3] - p0x, e1y = tri[4] - p0y, e1z = tri[5] - p0z;
  const e2x = tri[6] - p0x, e2y = tri[7] - p0y, e2z = tri[8] - p0z;
  const pvx = y * e2z - z * e2y, pvy = z * e2x - x * e2z, pvz = x * e2y - y * e2x;
  const det = e1x * pvx + e1y * pvy + e1z * pvz;
  if (det > -1e-12 && det < 1e-12) return false;
  const inv = 1 / det;
  const u = (-p0x * pvx - p0y * pvy - p0z * pvz) * inv;
  if (!(u > HINT_MARGIN)) return false;
  const qvx = -p0y * e1z + p0z * e1y, qvy = -p0z * e1x + p0x * e1z, qvz = -p0x * e1y + p0y * e1x;
  const v = (x * qvx + y * qvy + z * qvz) * inv;
  if (!(v > HINT_MARGIN) || !(u + v < 1 - HINT_MARGIN)) return false;
  return (e2x * qvx + e2y * qvy + e2z * qvz) * inv > 0;
}

/** 62.51: a hint is trusted only this far inside its triangle (barycentric), a thousand times BARY_EPS. */
const HINT_MARGIN = 1e-3;
/** rayTriangle's other outputs: the hit's barycentric (u, v), and whether the edge tests passed (the distance was reached). */
let hitRan = false;
/** (u, v) of rayTriangle's last hit — 62.57: in a typed array, not module `let`s (a double stored to one was boxed each hit). */
const hitUV = new Float64Array(2);

/**
 * 62.64: rayTriangle's ray in (the unit direction from the centre, [0..2]) and its hit distance out ([3]) — a module
 * buffer, not arguments and a return: a double passed to or returned from a call TurboFan does not inline is boxed (four
 * heap numbers a triangle tried, ~340 KB a tick at 32 a side's fight). Set once a lookup; the same numbers either way.
 */
const ray = new Float64Array(4);

/**
 * Möller–Trumbore from the planet centre along the unit `ray` [0..2] against mesh triangle `t` displaced by `heightmap`:
 * the hit distance into `ray[3]`, or 0 when the ray misses (det near zero, or outside the edges by more than BARY_EPS —
 * `hitRan` false). One function for the lookup's loop and its hint, so both do the same arithmetic.
 */
function rayTriangle(heightmap: Float32Array, bp: Float32Array, inv: Float64Array | Float32Array, t: number): void {
  hitRan = false;
  const dx = ray[0], dy = ray[1], dz = ray[2];
  const i0 = t * 3, i1 = i0 + 1, i2 = i0 + 2;

  // Displaced vertex positions: base + unit(base) * h
  const s0 = 1 + heightmap[i0] * inv[i0];
  const s1 = 1 + heightmap[i1] * inv[i1];
  const s2 = 1 + heightmap[i2] * inv[i2];
  const p0x = bp[i0 * 3] * s0, p0y = bp[i0 * 3 + 1] * s0, p0z = bp[i0 * 3 + 2] * s0;
  const p1x = bp[i1 * 3] * s1, p1y = bp[i1 * 3 + 1] * s1, p1z = bp[i1 * 3 + 2] * s1;
  const p2x = bp[i2 * 3] * s2, p2y = bp[i2 * 3 + 1] * s2, p2z = bp[i2 * 3 + 2] * s2;

  // Möller–Trumbore, ray origin at planet centre (0,0,0)
  const e1x = p1x - p0x, e1y = p1y - p0y, e1z = p1z - p0z;
  const e2x = p2x - p0x, e2y = p2y - p0y, e2z = p2z - p0z;
  const pvx = dy * e2z - dz * e2y;
  const pvy = dz * e2x - dx * e2z;
  const pvz = dx * e2y - dy * e2x;
  const det = e1x * pvx + e1y * pvy + e1z * pvz;
  if (det > -1e-12 && det < 1e-12) { ray[3] = 0; return; }
  const invDet = 1 / det;
  const tvx = -p0x, tvy = -p0y, tvz = -p0z;
  const u = (tvx * pvx + tvy * pvy + tvz * pvz) * invDet;
  if (u < -BARY_EPS || u > 1 + BARY_EPS) { ray[3] = 0; return; }
  const qvx = tvy * e1z - tvz * e1y;
  const qvy = tvz * e1x - tvx * e1z;
  const qvz = tvx * e1y - tvy * e1x;
  const v = (dx * qvx + dy * qvy + dz * qvz) * invDet;
  if (v < -BARY_EPS || u + v > 1 + BARY_EPS) { ray[3] = 0; return; }
  hitUV[0] = u; hitUV[1] = v; hitRan = true;
  ray[3] = (e2x * qvx + e2y * qvy + e2z * qvz) * invDet;
}

export function getSurfaceRadiusRaw(
  heightmap: Float32Array,
  basePositions: Float32Array,
  radius: number,
  dx: number, dy: number, dz: number,
): number {
  return surfaceRadiusWith(getTerrainIndex(basePositions), heightmap, basePositions, radius, dx, dy, dz);
}

/** getSurfaceRadiusRaw with the mesh's terrain index already in hand (62.51: a sight line fetches it once, not per sample). */
export function surfaceRadiusWith(
  idx: TerrainIndex,
  heightmap: Float32Array,
  basePositions: Float32Array,
  radius: number,
  dx: number, dy: number, dz: number,
): number {
  look[0] = dx; look[1] = dy; look[2] = dz; look[3] = radius;
  surfaceRadiusLook(idx, heightmap, basePositions);
  return look[4];
}

/**
 * 62.64: the lookup's way in and out for the hot callers (a sight line's samples, the march, a person's ground): the
 * direction from the centre [0..2] and the planet's radius [3] in, the surface radius out [4] — a module buffer, not
 * arguments and a return (a double crossing a call TurboFan does not inline is boxed: five heap numbers a lookup, and a
 * sight line makes ~3,000 a tick at 32 a side). `boundLook` reads the same inputs. surfaceRadiusWith is this, wrapped.
 */
export const look = new Float64Array(5);

/** surfaceRadiusWith over `look`: [0..3] in, [4] out. */
export function surfaceRadiusLook(idx: TerrainIndex, heightmap: Float32Array, basePositions: Float32Array): void {
  const radius = look[3];
  let dx = look[0], dy = look[1], dz = look[2];
  const dl = Math.sqrt(dx * dx + dy * dy + dz * dz);
  if (dl < 1e-12) { lastTriangle = -1; look[4] = radius; return; }
  dx /= dl; dy /= dl; dz /= dl;

  const bp = basePositions;
  const inv = idx.invLen;

  if (idx.valid) {
    ray[0] = dx; ray[1] = dy; ray[2] = dz;
    // 62.51: the triangle the last lookup on this mesh hit, first — a sight line's samples mostly fall in one. A ray from
    // the centre crosses one triangle of a radially displaced mesh; one well inside it (HINT_MARGIN, far past BARY_EPS) can
    // meet no other in the bin, so the loop below would return this same triangle and value: the same answer, sooner
    const hint = idx.lastHit ?? -1;
    if (hint >= 0) {
      rayTriangle(heightmap, bp, inv, hint);
      const tHit = ray[3];
      if (tHit > 0 && hitUV[0] > HINT_MARGIN && hitUV[1] > HINT_MARGIN && hitUV[0] + hitUV[1] < 1 - HINT_MARGIN) { lastTriangle = hint; look[4] = tHit; return; }
    }
    const bin = dirToBin(dx, dy, dz, idx.n);
    const start = idx.binStart[bin], end = idx.binStart[bin + 1];
    let bestDot = -2, bestIdx = -1;

    for (let k = start; k < end; k++) {
      const t = idx.binTris[k];
      const i0 = t * 3, i2 = i0 + 2;
      rayTriangle(heightmap, bp, inv, t);
      const tHit = ray[3];
      if (tHit > 0) { lastTriangle = t; idx.lastHit = t; look[4] = tHit; return; }
      if (!hitRan) continue;   // (rejected before the edges: no fallback vertex considered, as before)

      // Keep nearest candidate vertex as fallback
      for (let vi = i0; vi <= i2; vi++) {
        const d = (bp[vi * 3] * dx + bp[vi * 3 + 1] * dy + bp[vi * 3 + 2] * dz) * inv[vi];
        if (d > bestDot) { bestDot = d; bestIdx = vi; }
      }
    }
    if (bestIdx >= 0) { lastTriangle = -1; look[4] = radius + heightmap[bestIdx]; return; }
  }

  // Legacy nearest-vertex scan (non-mesh data or empty bin)
  let bestDot = -2, bestIdx = 0;
  const count = heightmap.length;
  for (let i = 0; i < count; i++) {
    const d = (bp[i * 3] * dx + bp[i * 3 + 1] * dy + bp[i * 3 + 2] * dz) * inv[i];
    if (d > bestDot) { bestDot = d; bestIdx = i; }
  }
  lastTriangle = -1;
  look[4] = radius + heightmap[bestIdx];
}

/**
 * 62.51: an upper bound on getSurfaceRadiusRaw in a direction, cheap — the farthest displaced vertex of the triangles in
 * the direction's bin (a ray's hit is a convex combination of one of them, give or take BARY_EPS at an edge, hence the
 * margin; the in-bin fallback returns one of their vertices). An empty bin, or data that is not a mesh, falls back to
 * the scan over every vertex: the bound is then the planet-wide maximum. Cached per heightmap (edits are copy-on-write:
 * a crater makes a new array). For a caller that only needs to know the ground is well below a point.
 */
const boundCache = new WeakMap<Float32Array, SurfaceTables>();
/** 62.51: a mesh's terrain index and its bound table (surfaceRadiusBound's), fetched once for many lookups. */
export interface SurfaceTables {
  idx: TerrainIndex; bp: Float32Array; byBin: Float64Array | null; all: number;
  /** 62.56's follow-up: a vertex where `all` is reached, and the radius the bounds were taken at (a patch's checks) */
  allAt: number; radius: number;
}
export function surfaceTables(heightmap: Float32Array, basePositions: Float32Array, radius: number): SurfaceTables {
  let c = boundCache.get(heightmap);
  if (!c || c.bp !== basePositions) {
    const e = tableEdits.get(heightmap);
    c = e && e.prev.bp === basePositions && e.prev.radius === radius ? patchTables(e.prev, heightmap, e.vertices) : buildTables(heightmap, basePositions, radius);
    tableEdits.delete(heightmap);
    boundCache.set(heightmap, c);
  }
  return c;
}

/** A vertex's displaced radius, the bound's measure (the larger of the mesh's and the surface's at it). */
function displaced(bp: Float32Array, inv: Float64Array, heightmap: Float32Array, radius: number, i: number): number {
  return Math.max(Math.sqrt(bp[i * 3] * bp[i * 3] + bp[i * 3 + 1] * bp[i * 3 + 1] + bp[i * 3 + 2] * bp[i * 3 + 2]) * (1 + heightmap[i] * inv[i]), radius + heightmap[i]);
}

/** One bin's bound: its triangles' farthest displaced vertex (the planet-wide `all` for an empty bin). */
function binBound(idx: TerrainIndex, bp: Float32Array, heightmap: Float32Array, radius: number, b: number, all: number): number {
  if (!(idx.binStart[b + 1] > idx.binStart[b])) return all;
  let m = -Infinity;
  for (let k = idx.binStart[b]; k < idx.binStart[b + 1]; k++) {
    const t = idx.binTris[k] * 3;
    m = Math.max(m, displaced(bp, idx.invLen, heightmap, radius, t), displaced(bp, idx.invLen, heightmap, radius, t + 1), displaced(bp, idx.invLen, heightmap, radius, t + 2));
  }
  return m;
}

function buildTables(heightmap: Float32Array, basePositions: Float32Array, radius: number): SurfaceTables {
  simWork.surfaceTableBuilds++;
  const idx = getTerrainIndex(basePositions), bp = basePositions;
  let all = -Infinity, allAt = -1;
  for (let i = 0; i < heightmap.length; i++) { const v = displaced(bp, idx.invLen, heightmap, radius, i); if (v > all) { all = v; allAt = i; } }
  let byBin: Float64Array | null = null;
  if (idx.valid) {
    const bins = idx.binStart.length - 1;
    byBin = new Float64Array(bins);
    for (let b = 0; b < bins; b++) byBin[b] = binBound(idx, bp, heightmap, radius, b, all);
  }
  return { idx, bp, byBin, all, allAt, radius };
}

/**
 * 62.56's follow-up (the landing tick: the first sight line after a crater rebuilt these over the whole planet, 6.9 ms):
 * a crater's heightmap → the tables of the heightmap it was dug from and the vertices whose heights it changed. The
 * tables, not the old heightmap, are kept (a chain of a match's heightmaps would stay alive); a crater dug from a
 * heightmap whose tables were never asked for chains onto that one's edit. Noted by resolveImpact.
 */
const tableEdits = new WeakMap<Float32Array, { prev: SurfaceTables; vertices: readonly number[] }>();
export function noteHeightmapEdit(from: Float32Array, to: Float32Array, vertices: readonly number[]): void {
  if (from === to) return;
  const prev = boundCache.get(from);
  if (prev) { tableEdits.set(to, { prev, vertices }); return; }
  const chained = tableEdits.get(from);
  if (chained) tableEdits.set(to, { prev: chained.prev, vertices: [...chained.vertices, ...vertices] });
}

/** The triangles' bins (the index's CSR read the other way), for a patch's touched bins. */
const triBinsCache = new WeakMap<TerrainIndex, { start: Uint32Array; bins: Uint32Array }>();
function triBinsOf(idx: TerrainIndex): { start: Uint32Array; bins: Uint32Array } {
  let r = triBinsCache.get(idx);
  if (!r) {
    const bins = idx.binStart.length - 1, start = new Uint32Array(idx.triCount + 1);
    for (let k = 0; k < idx.binTris.length; k++) start[idx.binTris[k] + 1]++;
    for (let t = 0; t < idx.triCount; t++) start[t + 1] += start[t];
    const fill = start.slice(0, idx.triCount), out = new Uint32Array(idx.binTris.length);
    for (let b = 0; b < bins; b++) for (let k = idx.binStart[b]; k < idx.binStart[b + 1]; k++) out[fill[idx.binTris[k]]++] = b;
    r = { start, bins: out };
    triBinsCache.set(idx, r);
  }
  return r;
}

/** Build a mesh's terrain index and its triangles' bins now (match setup), not on the first crater's patch (3.7 ms). */
export function prepareSurfaceTables(basePositions: Float32Array): void {
  const idx = getTerrainIndex(basePositions);
  if (idx.valid) triBinsOf(idx);
}

/**
 * The tables of `heightmap` from `prev`'s, `vertices` being every vertex whose height differs between them — the same
 * numbers a whole build makes (tests/surfaceTablesPatch.test.ts): the bins holding a triangle of a changed vertex are
 * measured again; `all` is the larger of `prev`'s and the changed vertices' when `prev`'s was reached at an unchanged
 * vertex (else the planet is measured again), and an empty bin, which reads `all`, follows it.
 */
function patchTables(prev: SurfaceTables, heightmap: Float32Array, vertices: readonly number[]): SurfaceTables {
  const { idx, bp, radius } = prev, inv = idx.invLen;
  const changed = new Set(vertices);
  let all = prev.all, allAt = prev.allAt;
  if (allAt < 0 || changed.has(allAt)) {
    all = -Infinity; allAt = -1;
    for (let i = 0; i < heightmap.length; i++) { const v = displaced(bp, inv, heightmap, radius, i); if (v > all) { all = v; allAt = i; } }
  } else {
    // (ascending, so a tie keeps the lowest vertex, as a whole build's scan does)
    for (const i of [...changed].sort((a, b) => a - b)) { const v = displaced(bp, inv, heightmap, radius, i); if (v > all || (v === all && i < allAt)) { all = v; allAt = i; } }
  }
  if (!prev.byBin) { simWork.surfaceTablePatches++; return { idx, bp, byBin: null, all, allAt, radius }; }
  const byBin = new Float64Array(prev.byBin);
  const tb = triBinsOf(idx), touched = new Set<number>();
  for (const i of changed) { const t = Math.floor(i / 3); if (t < idx.triCount) for (let k = tb.start[t]; k < tb.start[t + 1]; k++) touched.add(tb.bins[k]); }
  for (const b of touched) byBin[b] = binBound(idx, bp, heightmap, radius, b, all);
  if (all !== prev.all) for (let b = 0; b < byBin.length; b++) if (!(idx.binStart[b + 1] > idx.binStart[b])) byBin[b] = all;
  simWork.surfaceTablePatches++;
  return { idx, bp, byBin, all, allAt, radius };
}

/** surfaceRadiusBound with the tables in hand. */
export function boundWith(c: SurfaceTables, radius: number, dx: number, dy: number, dz: number): number {
  const margin = 1e-4 * radius;
  if (!c.byBin) return c.all + margin;
  const dl = Math.sqrt(dx * dx + dy * dy + dz * dz);
  if (dl < 1e-12) return Math.max(radius, c.all) + margin;
  // the same bin getSurfaceRadiusRaw looks in (normalised the same way)
  dx /= dl; dy /= dl; dz /= dl;
  return c.byBin[dirToBin(dx, dy, dz, c.idx.n)] + margin;
}

/** 62.64: boundWith over `look` ([0..2] the direction, [3] the radius), the bound into [4] — the same arithmetic. */
export function boundLook(c: SurfaceTables): void {
  const radius = look[3];
  const margin = 1e-4 * radius;
  if (!c.byBin) { look[4] = c.all + margin; return; }
  let dx = look[0], dy = look[1], dz = look[2];
  const dl = Math.sqrt(dx * dx + dy * dy + dz * dz);
  if (dl < 1e-12) { look[4] = Math.max(radius, c.all) + margin; return; }
  dx /= dl; dy /= dl; dz /= dl;
  look[4] = c.byBin[dirToBin(dx, dy, dz, c.idx.n)] + margin;
}

export function surfaceRadiusBound(heightmap: Float32Array, basePositions: Float32Array, radius: number, dx: number, dy: number, dz: number): number {
  return boundWith(surfaceTables(heightmap, basePositions, radius), radius, dx, dy, dz);
}

/** Surface radius of a planet in a direction (world or local — only direction matters). */
export function getSurfaceRadius(planet: PlanetState, direction: Vec3): number {
  if (!planet) throw new Error('getSurfaceRadius: planet is undefined');
  simWork.groundLookups++;   // 62.53: counted for the frame budget
  return getSurfaceRadiusRaw(planet.heightmap, planet.basePositions, planet.radius, direction.x, direction.y, direction.z);
}

/**
 * Index of the mesh vertex nearest to a direction. O(bin) through the spatial
 * index (the nearest vertex lies in the hit triangle's one-ring, which the
 * bin's ±1 margin covers); full scan for non-mesh data.
 */
export function nearestSurfaceVertex(planet: PlanetState, direction: Vec3): number {
  const bp = planet.basePositions;
  const count = planet.heightmap.length;
  let dx = direction.x, dy = direction.y, dz = direction.z;
  const dl = Math.sqrt(dx * dx + dy * dy + dz * dz);
  if (dl < 1e-12 || count === 0) return 0;
  dx /= dl; dy /= dl; dz /= dl;

  const idx = getTerrainIndex(bp);
  const inv = idx.invLen;
  let bestDot = -2, bestIdx = -1;
  if (idx.valid) {
    const bin = dirToBin(dx, dy, dz, idx.n);
    for (let k = idx.binStart[bin]; k < idx.binStart[bin + 1]; k++) {
      const t = idx.binTris[k];
      for (let vi = t * 3; vi < t * 3 + 3; vi++) {
        const d = (bp[vi * 3] * dx + bp[vi * 3 + 1] * dy + bp[vi * 3 + 2] * dz) * inv[vi];
        if (d > bestDot) { bestDot = d; bestIdx = vi; }
      }
    }
    if (bestIdx >= 0) return bestIdx;
  }
  for (let i = 0; i < count; i++) {
    const d = (bp[i * 3] * dx + bp[i * 3 + 1] * dy + bp[i * 3 + 2] * dz) * inv[i];
    if (d > bestDot) { bestDot = d; bestIdx = i; }
  }
  return bestIdx < 0 ? 0 : bestIdx;
}

/** World-space surface point of a planet in a direction. */
export function getSurfacePoint(planet: PlanetState, direction: Vec3): Vec3 {
  const dl = Math.sqrt(direction.x * direction.x + direction.y * direction.y + direction.z * direction.z);
  if (dl < 1e-12) return { ...planet.position };
  const r = getSurfaceRadius(planet, direction);
  return {
    x: planet.position.x + direction.x / dl * r,
    y: planet.position.y + direction.y / dl * r,
    z: planet.position.z + direction.z / dl * r,
  };
}

/** Outer bounding shell: nothing of the terrain can lie beyond this radius. */
export function getBlockingShellRadius(planet: PlanetState): number {
  return planet.radius * (1 + MAX_TERRAIN_HEIGHT_FRACTION);
}

// =============================================================================
// Terrain raycast (hitscan weapons)
// =============================================================================

export interface TerrainRayHit {
  planetIndex: number;
  distance: number;
  point: Vec3;
}

/** The terrain march's step (world units); core/horizonCull.ts reasons about its samples. */
export const RAY_MARCH_STEP = 0.1;

/**
 * First terrain hit along a ray. Marches inside each planet's bounding shell
 * and refines the crossing by bisection.
 */
/** raycastTerrain's test: is the point `t` along the ray (planet-relative start, unit direction) inside the ground? */
function insideAt(planet: PlanetState, ocx: number, ocy: number, ocz: number, dx: number, dy: number, dz: number, t: number): boolean {
  const px = ocx + dx * t, py = ocy + dy * t, pz = ocz + dz * t;
  const dist = Math.sqrt(px * px + py * py + pz * pz);
  look[0] = px; look[1] = py; look[2] = pz; look[3] = planet.radius;   // (62.64: getSurfaceRadiusRaw over the buffer)
  surfaceRadiusLook(getTerrainIndex(planet.basePositions), planet.heightmap, planet.basePositions);
  return dist <= look[4];
}

export function raycastTerrain(
  start: Vec3,
  dir: Vec3,
  maxDist: number,
  planets: PlanetState[],
): TerrainRayHit | null {
  const dl = Math.sqrt(dir.x * dir.x + dir.y * dir.y + dir.z * dir.z);
  if (dl < 1e-12) return null;
  const dx = dir.x / dl, dy = dir.y / dl, dz = dir.z / dl;

  let best: TerrainRayHit | null = null;

  for (let pi = 0; pi < planets.length; pi++) {
    const planet = planets[pi];
    if (!planet.alive) continue;
    const shell = getBlockingShellRadius(planet);

    const ocx = start.x - planet.position.x;
    const ocy = start.y - planet.position.y;
    const ocz = start.z - planet.position.z;
    const b = ocx * dx + ocy * dy + ocz * dz;
    const c = ocx * ocx + ocy * ocy + ocz * ocz - shell * shell;
    const disc = b * b - c;
    if (disc < 0) continue;
    const sq = Math.sqrt(disc);
    const t0 = -b - sq, t1 = -b + sq;
    if (t1 <= 0) continue;
    const tStart = Math.max(t0, 0);
    const tEnd = Math.min(t1, best ? best.distance : maxDist);
    if (tStart >= tEnd) continue;


    let prev = tStart;
    if (insideAt(planet, ocx, ocy, ocz, dx, dy, dz, prev)) {
      // Ray starts inside terrain of this planet — treat as immediate hit
      best = { planetIndex: pi, distance: prev, point: { x: start.x + dx * prev, y: start.y + dy * prev, z: start.z + dz * prev } };
      continue;
    }
    let t = prev + RAY_MARCH_STEP;
    let hit = -1;
    const idx = getTerrainIndex(planet.basePositions);
    while (t <= tEnd) {
      if (insideAt(planet, ocx, ocy, ocz, dx, dy, dz, t)) { hit = t; break; }
      // 62.51: the steps ahead whose points stay over this ground triangle, above it (an exact bound): no lookups for
      // them — t still advances by the same additions, so every later t, and the hit, are the same numbers
      const tri = lastTriangle;
      let skip = tri >= 0 ? marchClearSteps(planet, idx, tri, ocx + dx * t, ocy + dy * t, ocz + dz * t, dx * RAY_MARCH_STEP, dy * RAY_MARCH_STEP, dz * RAY_MARCH_STEP) : 0;
      prev = t;
      t += RAY_MARCH_STEP;
      while (skip > 0 && t <= tEnd) { prev = t; t += RAY_MARCH_STEP; skip--; }
    }
    if (hit < 0) continue;

    // Bisect between prev (outside) and hit (inside)
    let lo = prev, hi = hit;
    for (let i = 0; i < 10; i++) {
      const mid = (lo + hi) * 0.5;
      if (insideAt(planet, ocx, ocy, ocz, dx, dy, dz, mid)) hi = mid; else lo = mid;
    }
    best = { planetIndex: pi, distance: hi, point: { x: start.x + dx * hi, y: start.y + dy * hi, z: start.z + dz * hi } };
  }

  return best;
}
